import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserPlus, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin-only button that calls POST /api/admin/seed-showcase-accounts to
 * (re)create the four marketing showcase users — three students (7+, 11+,
 * GCSE) and one parent linked to all three. Idempotent: safe to tap multiple
 * times; the endpoint upserts so existing records are refreshed rather than
 * duplicated.
 *
 * Mobile-first: a single tap activates the showcase set, with the resulting
 * credential card rendered inline so the user can copy-paste straight from
 * the page (great on a phone in Chrome where DevTools isn't an option).
 */
const ShowcaseSeedCard = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSeed = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/seed-showcase-accounts`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(data);
      toast.success('Showcase accounts ready — all 4 seeded.');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Seeding failed.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow-xl border-2 border-purple-200" data-testid="showcase-seed-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-purple-600" />
          Marketing Showcase Accounts
        </CardTitle>
        <CardDescription>
          One-tap seed for the four demo accounts — 7+, 11+, GCSE student + a
          parent linked to all three. Idempotent: re-tap anytime to refresh.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button
          onClick={handleSeed}
          disabled={loading}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="showcase-seed-btn"
        >
          {loading ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Seeding…</>
          ) : (
            <><UserPlus className="mr-2 h-4 w-4" /> Seed / Refresh Showcase Accounts</>
          )}
        </Button>

        {result && (
          <div
            className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-3"
            data-testid="showcase-seed-result"
          >
            <p className="flex items-center gap-2 text-emerald-700 font-semibold">
              <CheckCircle2 className="h-5 w-5" /> {result.message}
            </p>
            <div className="text-sm">
              <p className="font-semibold mb-1">Universal password:</p>
              <code className="px-2 py-1 bg-white rounded border text-purple-700 font-mono">
                {result.password}
              </code>
            </div>
            <div className="text-sm">
              <p className="font-semibold mb-2">Accounts (active until {result.expires?.slice(0, 10)}):</p>
              <ul className="space-y-1.5 list-disc list-inside">
                {result.accounts?.map((a) => (
                  <li key={a.email} className="font-mono text-xs break-all">
                    <span className="font-sans font-semibold uppercase text-purple-700 mr-2">
                      {a.role === 'parent' ? 'PARENT' : a.stage}
                    </span>
                    {a.email}
                    {a.tier ? ` · ${a.tier}` : ''}
                    {a.linked_children ? ` · ${a.linked_children} kids linked` : ''}
                  </li>
                ))}
              </ul>
            </div>

            {result.diagnostics && (
              <div className="text-xs border-t border-emerald-200 pt-3 mt-3">
                <p className="font-semibold mb-2">Self-test (password check vs stored hash):</p>
                <ul className="space-y-1 font-mono">
                  {result.diagnostics.map((d) => (
                    <li key={d.email} className={d.ok ? 'text-emerald-700' : 'text-rose-700'}>
                      {d.ok ? '✓' : '✗'} {d.email}
                      {d.reason ? ` — ${d.reason}` : ''}
                      {d.matches > 1 ? ` (⚠ ${d.matches} duplicate records)` : ''}
                      {d.parent_emailVerified === false ? ' (⚠ parent not email-verified)' : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ShowcaseSeedCard;
